import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, ChartBar, Users, CalendarBlank, Lightning, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => (v || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 });

const GanttSimulation = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [mode, setMode] = useState('time_driven');
  const [targetDate, setTargetDate] = useState('');
  const [manpowerPerDay, setManpowerPerDay] = useState(10);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/projects/${projectId}`, { withCredentials: true })
      .then(r => {
        setProject(r.data);
        if (r.data.project_end_date) setTargetDate(r.data.project_end_date);
      }).catch(() => toast.error('Failed to load project'));
  }, [projectId]);

  const runSimulation = useCallback(async () => {
    setLoading(true);
    try {
      const payload = { mode };
      if (mode === 'time_driven') payload.target_date = targetDate;
      else payload.manpower_per_day = Number(manpowerPerDay);
      const { data } = await axios.post(`${BACKEND}/api/projects/${projectId}/gantt-simulation`, payload, { withCredentials: true });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  }, [projectId, mode, targetDate, manpowerPerDay]);

  const feasColor = (f) => f === 'comfortable' ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : f === 'tight' ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-red-700 bg-red-50 border-red-200';

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="gantt-simulation">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4" data-testid="gantt-back">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-center gap-3 mb-1">
        <ChartBar size={22} weight="fill" className="text-blue-600" />
        <h1 className="text-lg font-bold text-zinc-900">Gantt Simulation</h1>
      </div>
      <p className="text-xs text-zinc-400 mb-6">{project?.pi_number || ''} — {project?.client_name || ''}</p>

      {/* Mode Toggle */}
      <div className="flex gap-2 mb-5" data-testid="gantt-mode-toggle">
        <button
          onClick={() => { setMode('time_driven'); setResult(null); }}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md border transition-all ${mode === 'time_driven' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
          data-testid="mode-time-driven"
        >
          <CalendarBlank size={14} /> Time-Driven
        </button>
        <button
          onClick={() => { setMode('manpower_driven'); setResult(null); }}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md border transition-all ${mode === 'manpower_driven' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
          data-testid="mode-manpower-driven"
        >
          <Users size={14} /> Manpower-Driven
        </button>
      </div>

      {/* Input */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-5">
        {mode === 'time_driven' ? (
          <div>
            <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Target Completion Date</label>
            <Input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
              className="mt-1.5 max-w-xs"
              data-testid="input-target-date"
            />
            <p className="text-[11px] text-zinc-400 mt-1">System will calculate required daily manpower</p>
          </div>
        ) : (
          <div>
            <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Available Manpower Per Day</label>
            <Input
              type="number"
              min={1}
              value={manpowerPerDay}
              onChange={e => setManpowerPerDay(e.target.value)}
              className="mt-1.5 max-w-xs"
              data-testid="input-manpower"
            />
            <p className="text-[11px] text-zinc-400 mt-1">System will estimate completion duration</p>
          </div>
        )}

        <Button
          onClick={runSimulation}
          disabled={loading || (mode === 'time_driven' && !targetDate)}
          className="mt-4 rounded-md"
          data-testid="run-simulation"
        >
          <Lightning size={14} className="mr-1.5" />
          {loading ? 'Calculating...' : 'Run Simulation'}
        </Button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4" data-testid="simulation-results">
          {/* Disclaimer */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Info size={16} className="text-blue-500 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-700">{result.disclaimer}</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-zinc-200 rounded-lg p-3">
              <div className="text-[10px] text-zinc-400 uppercase font-semibold">Total Man-Days</div>
              <div className="text-lg font-bold text-zinc-800 mt-0.5">{fmt(result.total_estimated_man_days)}</div>
              <div className="text-[10px] text-zinc-400">Source: {result.data_source === 'manpower_recipe' ? 'Recipe data' : 'BOQ estimate'}</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-lg p-3">
              <div className="text-[10px] text-zinc-400 uppercase font-semibold">Completed</div>
              <div className="text-lg font-bold text-emerald-700 mt-0.5">{fmt(result.completed_man_days)}</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-lg p-3">
              <div className="text-[10px] text-zinc-400 uppercase font-semibold">Remaining</div>
              <div className="text-lg font-bold text-amber-700 mt-0.5">{fmt(result.remaining_man_days)}</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-lg p-3">
              <div className="text-[10px] text-zinc-400 uppercase font-semibold">VO Man-Days</div>
              <div className="text-lg font-bold text-violet-700 mt-0.5">{fmt(result.vo_man_days)}</div>
            </div>
          </div>

          {/* Mode-specific output */}
          {mode === 'time_driven' && result.required_manpower_per_day !== undefined && (
            <div className="bg-white border border-zinc-200 rounded-lg p-5">
              <div className="text-xs font-semibold text-zinc-600 uppercase mb-3">Time-Driven Result</div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Working Days</div>
                  <div className="text-xl font-bold text-zinc-800">{result.working_days}</div>
                  <div className="text-[10px] text-zinc-400">{result.available_calendar_days} calendar days</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Required Manpower/Day</div>
                  <div className="text-xl font-bold text-zinc-800">{fmt(result.required_manpower_per_day)}</div>
                  <div className="text-[10px] text-zinc-400">workers needed daily</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Feasibility</div>
                  <div className={`inline-block mt-1 px-3 py-1 text-sm font-bold rounded-md border ${feasColor(result.feasibility)}`} data-testid="feasibility-badge">
                    {result.feasibility?.charAt(0).toUpperCase() + result.feasibility?.slice(1)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {mode === 'manpower_driven' && result.estimated_completion_date && (
            <div className="bg-white border border-zinc-200 rounded-lg p-5">
              <div className="text-xs font-semibold text-zinc-600 uppercase mb-3">Manpower-Driven Result</div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Estimated Completion</div>
                  <div className="text-xl font-bold text-zinc-800">{result.estimated_completion_date}</div>
                  <div className="text-[10px] text-zinc-400">{result.calendar_days_needed} calendar days</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Working Days</div>
                  <div className="text-xl font-bold text-zinc-800">{result.working_days_needed}</div>
                  <div className="text-[10px] text-zinc-400">at {result.manpower_per_day} workers/day</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Feasibility</div>
                  <div className={`inline-block mt-1 px-3 py-1 text-sm font-bold rounded-md border ${feasColor(result.feasibility)}`} data-testid="feasibility-badge">
                    {result.feasibility?.charAt(0).toUpperCase() + result.feasibility?.slice(1)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trade Breakdown */}
          {result.trade_breakdown?.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg p-5">
              <div className="text-xs font-semibold text-zinc-600 uppercase mb-3">Trade Breakdown (Man-Days)</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-100">
                    <th className="text-left py-2 text-zinc-400 font-semibold uppercase">Trade</th>
                    <th className="text-left py-2 text-zinc-400 font-semibold uppercase">Description</th>
                    <th className="text-right py-2 text-zinc-400 font-semibold uppercase">Qty</th>
                    <th className="text-right py-2 text-zinc-400 font-semibold uppercase">Man-Days</th>
                  </tr>
                </thead>
                <tbody>
                  {result.trade_breakdown.map((t, i) => (
                    <tr key={i} className="border-b border-zinc-50">
                      <td className="py-2 text-zinc-700 font-medium">{t.trade}</td>
                      <td className="py-2 text-zinc-500">{t.description}</td>
                      <td className="py-2 text-right text-zinc-600">{fmt(t.quantity)} {t.unit}</td>
                      <td className="py-2 text-right font-semibold text-zinc-800">{fmt(t.man_days)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GanttSimulation;
